import React, { useState, useEffect, useRef } from "react";
import "./styles/_index.scss";
import store from "../../store";
import { useSelector } from "react-redux";
import SendIcon from "@mui/icons-material/Send";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import CloseIcon from "@mui/icons-material/Close";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import DownloadIcon from "@mui/icons-material/Download";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import BasicModal from "../../components/modal";
import useWebSocket from "react-use-websocket";
import axios from "axios";

interface ChatMessage {
  id: number;
  user_type: string;
  date_time: string;
  imgfile: string;
  file: any;
  file_name: string | null;
  message: string;
  token: string;
}

interface ImagePreviewModalProps {
  imageSrc: string;
  onClose: () => void;
}

const ImagePreviewModal: React.FC<ImagePreviewModalProps> = ({ imageSrc, onClose }) => {
  const [scale, setScale] = useState(1);
  const [dragging, setDragging] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [startPos, setStartPos] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [onClose]);

  const zoomIn = (e: React.MouseEvent) => { e.stopPropagation(); setScale((s) => Math.min(s + 0.25, 4)); };
  const zoomOut = (e: React.MouseEvent) => { e.stopPropagation(); setScale((s) => Math.max(s - 0.25, 0.5)); };
  const reset = (e: React.MouseEvent) => { e.stopPropagation(); setScale(1); setPosition({ x: 0, y: 0 }); };
  const handleDownload = (e: React.MouseEvent) => {
    e.stopPropagation();
    const a = document.createElement("a");
    a.href = imageSrc;
    a.download = "image";
    a.click();
  };
  const onMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setDragging(true);
    setStartPos({ x: e.clientX - position.x, y: e.clientY - position.y });
  };
  const onMouseMove = (e: React.MouseEvent) => {
    if (!dragging) return;
    setPosition({ x: e.clientX - startPos.x, y: e.clientY - startPos.y });
  };
  const onMouseUp = () => setDragging(false);

  const toolbarBtns = [
    { icon: <ZoomOutIcon fontSize="small" />, action: zoomOut, title: "Zoom out" },
    { icon: <ZoomInIcon fontSize="small" />, action: zoomIn, title: "Zoom in" },
    { icon: <RestartAltIcon fontSize="small" />, action: reset, title: "Reset" },
    { icon: <DownloadIcon fontSize="small" />, action: handleDownload, title: "Download" },
  ];

  const imgStyle: React.CSSProperties = {
    maxWidth: "88vw",
    maxHeight: "80vh",
    objectFit: "contain",
    borderRadius: 10,
    boxShadow: "0 12px 56px rgba(0,0,0,0.7)",
    transform: `scale(${scale})`,
    transformOrigin: "center center",
    transition: dragging ? "none" : "transform 0.2s ease",
    display: "block",
  };

  const wrapperStyle: React.CSSProperties = {
    cursor: dragging ? "grabbing" : scale > 1 ? "grab" : "default",
    userSelect: "none",
    transform: `translate(${position.x}px, ${position.y}px)`,
    transition: dragging ? "none" : "transform 0.15s ease",
  };

  return (
    <div className="tg-modal-overlay" onClick={onClose}>
      <div className="tg-modal-toolbar" onClick={(e) => e.stopPropagation()}>
        {toolbarBtns.map(({ icon, action, title }) => (
          <button key={title} onClick={action} title={title} className="tg-modal-btn">
            {icon}
          </button>
        ))}
        <div className="tg-modal-sep" />
        <span className="tg-modal-scale">{Math.round(scale * 100)}%</span>
        <div className="tg-modal-sep" />
        <button onClick={(e) => { e.stopPropagation(); onClose(); }} title="Close" className="tg-modal-btn tg-modal-btn--close">
          <CloseIcon fontSize="small" />
        </button>
      </div>
      <div
        className="tg-modal-imgwrap"
        onClick={(e) => e.stopPropagation()}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUp}
        onMouseLeave={onMouseUp}
        style={wrapperStyle}
      >
        <img src={imageSrc} alt="Preview" draggable={false} className="tg-modal-img" style={imgStyle} />
      </div>
      <p className="tg-modal-hint">Click outside or press Esc to close</p>
    </div>
  );
};

const formatMessageTime = (dateTimeString: string) => {
  let dateStr = dateTimeString;
  if (dateStr.includes(" ") && !dateStr.includes("T")) dateStr = dateStr.replace(" ", "T");
  if (!dateStr.includes("Z") && !dateStr.includes("+") && !dateStr.match(/-\d{2}:\d{2}$/)) {
    dateStr = dateStr.split(".")[0] + "Z";
  }
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return "Invalid time";
  return date.toLocaleTimeString([], {
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

const getChatIdentifier = (): string | null => {
  const uid = localStorage.getItem("user_id");
  const proj = localStorage.getItem("project");
  const fid = localStorage.getItem("fid");
  const sso = localStorage.getItem("sso_multi_client") === "true";
  if (sso && fid) return fid;
  if (uid && proj) return `${uid}_${encodeURIComponent(proj)}`;
  return null;
};

const ChatAppClient: React.FC = () => {
  const [message, setMessage] = useState<ChatMessage[]>([]);
  const [text, setText] = useState("");
  const [selectedFile, setSelectedFile] = useState<File[]>([]);
  const [chatRoom, setChatRoom] = useState("");
  const chatRoomRef = useRef("");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [fileUrl, setFileUrl] = useState<any[]>([]);
  const [showFilemodal, setShowFileModal] = useState(false);
  const msgRef = useRef<HTMLDivElement>(null);
  const appData = useSelector((state: any) => state?.app);
  const windowWidth = useRef(window.innerWidth);
  const [open, setOpen] = useState(windowWidth.current > 1000);
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [imagePreview, setImagePreview] = useState("");
  const [socketUrl, setSocketUrl] = useState<string | null>(null);
  const sendJsonMessageRef = useRef<any>(null);
  const [isTyping, setIsTyping] = useState(false);
  const typingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const updateChatRoom = (id: any) => {
    if (!id || id === "null" || id === "undefined") {
      // console.warn("Invalid chatRoom value:", id);
      return;
    }
    const roomId = String(id);
    setChatRoom(roomId);
    chatRoomRef.current = roomId;
  };

  const sendCreateChat = (sjm: any) => {
    if (!sjm) return;
    const _userId = localStorage.getItem("user_id");
    const _projectLocation = localStorage.getItem("project");
    const _forecastId = localStorage.getItem("fid");
    const _isSSO = localStorage.getItem("sso_multi_client") === "true";
    const _userName = localStorage.getItem("user_name");
    const _operation = localStorage.getItem("operation");
    if (_isSSO && _forecastId) {
      sjm({ mode: "createchat", user_id: _forecastId, user_type: "user", is_sso: true, forecast_id: _forecastId, user_name: _userName, operation: _operation, token: sessionStorage.getItem("token") });
    } else {
      sjm({ mode: "createchat", user_id: _userId, user_type: "user", is_sso: false, project_location: _projectLocation, token: sessionStorage.getItem("token") });
    }
  };

  useEffect(() => {
    const identifier = getChatIdentifier();
    if (identifier) {
      const url = `wss://${process.env.REACT_APP_WEBSOCKET_IP}/ws/chat/${identifier}/`;
      setSocketUrl(url);
    }
  }, []);

  useEffect(() => {
    store.dispatch({ type: "TOGGLE_MENU", payload: windowWidth.current > 1000 });
  }, []);

  useEffect(() => { setOpen(appData.toggle); }, [appData]);

  useEffect(() => {
    const interval = setInterval(() => {
      const activeChatRoom = chatRoomRef.current || localStorage.getItem("rooms");
      const _isSSO = localStorage.getItem("sso_multi_client") === "true";
      const _forecastId = localStorage.getItem("fid");
      const _userId = localStorage.getItem("user_id");
      const _projectLocation = localStorage.getItem("project");

      if (!activeChatRoom || !sendJsonMessageRef.current) return;

      if (_isSSO && _forecastId) {
        sendJsonMessageRef.current({
          mode: "latest",
          user_id: _forecastId,
          user_type: "user",
          is_sso: true,
          forecast_id: _forecastId,
          token: sessionStorage.getItem("token"),
          send_by: "user",
          select: "false"
        });
      } else {
        sendJsonMessageRef.current({
          mode: "latest",
          user_id: _userId,
          user_type: "user",
          project_location: _projectLocation,
          token: sessionStorage.getItem("token"),
          send_by: "user",
          select: "false"
        });
      }
    }, 10000);

    return () => clearInterval(interval);
  }, []);

  const { sendJsonMessage } = useWebSocket(socketUrl, {
     onOpen: () => { console.log("WebSocket opened:", socketUrl); },
    shouldReconnect: () => false,
    onMessage: (event) => processWebSocketMessages(event),
  });

  useEffect(() => { sendJsonMessageRef.current = sendJsonMessage; }, [sendJsonMessage]);

  const handleFileSelect = () => { if (fileInputRef.current) fileInputRef.current.click(); };

  function processWebSocketMessages(event: any) {
    const wsData = JSON.parse(event.data);
    const _isSSO = localStorage.getItem("sso_multi_client") === "true";
    const _forecastId = localStorage.getItem("fid");
    const _userId = localStorage.getItem("user_id");
    const _projectLocation = localStorage.getItem("project");

    const extractRoomId = (rooms: any): string | null => {
      if (!rooms) return null;
      if (typeof rooms === "string" && rooms !== "null") return rooms;
      if (typeof rooms === "number") return String(rooms);
      if (rooms?.chat_id) return String(rooms.chat_id);
      if (rooms?.id) return String(rooms.id);
      if (rooms?.forecast_id) return String(rooms.forecast_id);
      return null;
    };

    if (wsData.mode === "initchat" || (wsData.mode === "receivemsg" && !wsData.rooms && (!wsData.chats || wsData.chats.length === 0))) {
      sendCreateChat(sendJsonMessageRef.current);
      return;
    }

    if (wsData.mode === "receivemsg" && wsData.rooms) {
      const roomId = extractRoomId(wsData.rooms);
      if (roomId) { updateChatRoom(roomId); localStorage.setItem("rooms", roomId); }
      if (wsData.chats) setMessage(wsData.chats);
      msgRef?.current?.scrollIntoView({ behavior: "smooth" });
      if (roomId) sendJsonMessage({ mode: "readmessages", chat_id: roomId, user_type: "user" });
      return;
    }

    if (_isSSO && _forecastId) {
      if (wsData.mode === "createchat") {
        if (wsData.chats !== null) {
          setMessage(wsData.chats);
          const chatId = extractRoomId(wsData.rooms) || _forecastId;
          updateChatRoom(chatId);
          if (chatId) localStorage.setItem("rooms", chatId);
          msgRef?.current?.scrollIntoView({ behavior: "smooth" });
          sendJsonMessage({ mode: "readmessages", chat_id: chatId, user_type: "user" });
        }
      } else if (wsData.mode === "latest") {
        if (wsData.chats) {
          setMessage(wsData.chats);
          const chatId = extractRoomId(wsData.rooms) || _forecastId;
          updateChatRoom(chatId);
          msgRef?.current?.scrollIntoView({ behavior: "smooth" });
          sendJsonMessage({ mode: "readmessages", chat_id: chatId, user_type: "user" });
        }
      } else if (wsData.mode === "sendmsg") {
        sendJsonMessage({ mode: "latest", user_id: _forecastId, user_type: "user", is_sso: true, forecast_id: _forecastId, token: sessionStorage.getItem("token"), send_by: "user", select: "false" });
      }
    } else {
      if (wsData.mode === "createchat") {
        if (wsData.chats !== null) {
          setMessage(wsData.chats);
          const chatId = extractRoomId(wsData.rooms) || localStorage.getItem("rooms");
          updateChatRoom(chatId);
          if (chatId) localStorage.setItem("rooms", chatId);
          msgRef?.current?.scrollIntoView({ behavior: "smooth" });
          sendJsonMessage({ mode: "readmessages", chat_id: chatId, user_type: "user" });
        }
      } else if (wsData.mode === "latest") {
        if (wsData.chats) {
          setMessage(wsData.chats);
          const chatId = extractRoomId(wsData.rooms) || localStorage.getItem("rooms");
          updateChatRoom(chatId);
          msgRef?.current?.scrollIntoView({ behavior: "smooth" });
          sendJsonMessage({ mode: "readmessages", chat_id: chatId, user_type: "user" });
        }
      } else if (wsData.mode === "sendmsg") {
        sendJsonMessage({ mode: "latest", user_id: _userId, user_type: "user", project_location: _projectLocation, token: sessionStorage.getItem("token"), send_by: "user", select: "false" });
      }
    }
  }

  const saveFile = (e: any) => {
    const files = Array.from(e.target.files);
    setSelectedFile(files as File[]);
    if (files.length > 0) {
      const urls: any[] = [];
      files.forEach((file: any) => urls.push(URL.createObjectURL(file)));
      setFileUrl(urls);
      setShowFileModal(true);
    }
  };

  const sendData = async (event?: React.FormEvent) => {
    event?.preventDefault();
    const _isSSO = localStorage.getItem("sso_multi_client") === "true";
    const _forecastId = localStorage.getItem("fid");
    const _userId = localStorage.getItem("user_id");
    const _projectLocation = localStorage.getItem("project");
    const activeChatRoom = chatRoomRef.current || chatRoom || localStorage.getItem("rooms") || "";

    if (!activeChatRoom) {
      alert("Chat room not ready. Please wait a moment and try again.");
      return;
    }

    if (text !== "" || selectedFile.length > 0) {
      if (selectedFile.length > 0) {
        const formData = new FormData();
        if (_isSSO && _forecastId) {
          formData.append("user_id", _forecastId);
          formData.append("is_sso", "true");
          formData.append("forecast_id", _forecastId);
        } else {
          formData.append("user_id", _userId || "");
          formData.append("is_sso", "false");
          formData.append("project_location", _projectLocation || "");
        }
        formData.append("user_type", "user");
        formData.append("chat_id", activeChatRoom);
        formData.append("message", text);
        selectedFile.forEach((file) => formData.append("file", file));

        try {
          await axios.post(`${process.env.REACT_APP_BACKEND_IP}/api/sendmessage/`, formData, {
            headers: { Authorization: "Basic YWRtaW46YWRtaW4=" },
          });
        } catch (err: any) {
          if (!err?.response || err.response.status !== 500) {
            alert("Failed to send file. Please try again.");
            return;
          }
        }
        setText("");
        setSelectedFile([]);
        setShowFileModal(false);
        setFileUrl([]);
        const triggerLatest = () => {
          if (_isSSO && _forecastId) {
            sendJsonMessageRef.current({ mode: "latest", user_id: _forecastId, user_type: "user", is_sso: true, forecast_id: _forecastId, token: sessionStorage.getItem("token"), send_by: "user", select: "false" });
          } else {
            sendJsonMessageRef.current({ mode: "latest", user_id: _userId, user_type: "user", project_location: _projectLocation, token: sessionStorage.getItem("token"), send_by: "user", select: "false" });
          }
        };
        setTimeout(() => {
          triggerLatest();
          setTimeout(triggerLatest, 1500);
        }, 500);
      } else {
        if (_isSSO && _forecastId) {
          sendJsonMessage({ mode: "sendmessage", chat_id: activeChatRoom, message: text, user_type: "user", user_id: _forecastId, is_sso: true, forecast_id: _forecastId, send_by: "user" });
        } else {
          sendJsonMessage({ mode: "sendmessage", chat_id: activeChatRoom, message: text, user_type: "user", user_id: _userId, project_location: _projectLocation, send_by: "user" });
        }
        setText("");
        setSelectedFile([]);
        setShowFileModal(false);
        setFileUrl([]);
      }
    } else {
      alert("Message Cannot be null");
    }
  };

  return (
    <div style={open ? { marginLeft: 240, width: "calc(100% - 240px)", overflowX: "hidden" } : { overflowX: "hidden" }} className="main">
      <BasicModal
        open={showFilemodal}
        sendData={sendData}
        setText={setText}
        text={text}
        image={selectedFile}
        handleClose={() => { setShowFileModal(false); setFileUrl([]); }}
        handleOpen={() => setShowFileModal(true)}
      />
      {modalIsOpen && (
        <ImagePreviewModal imageSrc={imagePreview} onClose={() => setModalIsOpen(false)} />
      )}
      <div className="tg-page-wrap">
        <div className="tg-root">
          <div className="tg-chat-area">
            <div className="tg-messages">
              {message?.map((msgData, idx) => {
                const isImage = msgData.file?.startsWith("data:image/") || /\.(gif|jpe?g|tiff?|png|webp|bmp)$/i.test(msgData.file);
                const resolvedSrc = msgData.imgfile
                  ? `data:image/jpeg;base64,${msgData.imgfile}`
                  : msgData.file?.startsWith("data:")
                  ? msgData.file
                  : `${process.env.REACT_APP_BACKEND_IP}/uploads/${msgData.file}`;
                const isOut = msgData.user_type !== "admin";

                return (
                  <div key={idx} className={`tg-msg-row tg-msg-row--${isOut ? "out" : "in"}`}>
                    <div className={`tg-bubble tg-bubble--${isOut ? "out" : "in"} ${msgData.imgfile !== null ? (isImage ? "imga" : "file1") : ""}`}>
                      <div style={{ display: "flex", flexDirection: "column" }} className={msgData.file ? "" : "message_content"}>
                        {msgData.file ? (
                          isImage ? (
                            // eslint-disable-next-line jsx-a11y/img-redundant-alt
                            <img
                              src={resolvedSrc}
                              alt="Received Image"
                              className="tg-bubble__img"
                              onClick={() => { setModalIsOpen(true); setImagePreview(resolvedSrc); }}
                            />
                          ) : (
                            <a href={`${process.env.REACT_APP_BACKEND_IP}/uploads/${msgData.file}`} target="_blank" rel="noreferrer" className="tg-bubble__file">
                              <div className="tg-file-icon">
                                <DescriptionOutlinedIcon fontSize="small" />
                              </div>
                              <div className="tg-file-info">
                                <span className="tg-file-name">{msgData.file_name || msgData.file}</span>
                                <span className="tg-file-size">Document</span>
                              </div>
                            </a>
                          )
                        ) : null}
                        {msgData.imgfile !== null
                          ? message[idx]?.message === msgData?.message
                            ? null
                            : <span className="tg-bubble__text">{msgData?.message}</span>
                          : <span className="tg-bubble__text">{msgData?.message}</span>}
                        {msgData?.imgfile !== null
                          ? message[idx + 1]?.message === msgData?.message
                            ? null
                            : <span className="tg-bubble__text">{msgData?.message}</span>
                          : null}
                      </div>
                      <div className="tg-bubble__footer">
                        <span className="tg-bubble__time">{formatMessageTime(msgData.date_time)}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
              <div ref={msgRef} />
            </div>
            <form onSubmit={sendData} className="tg-input-bar">
              <input
                type="file"
                onChange={saveFile}
                multiple
                style={{ display: "none" }}
                ref={fileInputRef}
                accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx"
              />
              <div className="tg-input-attach" onClick={handleFileSelect}>
                <AttachFileIcon fontSize="small" />
              </div>
              <input
                className="tg-input-field"
                type="text"
                value={text}
                onChange={(e) => {
                  setText(e.target.value);
                  setIsTyping(true);
                  if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
                  typingTimeoutRef.current = setTimeout(() => setIsTyping(false), 1500);
                }}
                placeholder="Write a message..."
              />
              {isTyping && (
                <div className="tg-input-typing">
                  <span className="tg-input-typing__dot" />
                  <span className="tg-input-typing__dot" />
                  <span className="tg-input-typing__dot" />
                </div>
              )}
              <button
                className="tg-send-btn"
                type="submit"
                onClick={sendData}
                disabled={!chatRoomRef.current && !chatRoom && !localStorage.getItem("rooms")}
              >
                <SendIcon fontSize="small" />
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatAppClient;